// While a store subscription owns a group's plan, the server reports that store
// in billing_source and refuses every other plan purchase with HTTP 409
// managedByStore. Data add-ons stay available.
export const MANAGED_BY_STORE = 'managedByStore'

// One entry per store. A new store is an entry here plus its
// store_managed.<store> locale strings.
export const STORES = {
  appstore: { subscriptionsUrl: 'https://apps.apple.com/account/subscriptions' },
  googleplay: { subscriptionsUrl: 'https://play.google.com/store/account/subscriptions' },
} as const

export type StoreId = keyof typeof STORES

// store is the billing_source value (possibly a store this client does not
// know yet), or null when neither the refusal nor the services row names one.
export interface StoreManagement {
  store: string | null
  expiresAt: string | null
}

type Translate = (key: string, params?: Record<string, unknown>) => string

function knownStore(store: string | null): store is StoreId {
  return store != null && Object.hasOwn(STORES, store)
}

// The store that owns this services row's plan, with the end of the store's
// current period (reported under the row's key for that store). Null when the
// web owns it, including a subscription an admin released.
export function storeManagement(service: any): StoreManagement | null {
  const source = service?.billing_source
  if (!source || source === 'web') return null
  return { store: source, expiresAt: service?.[source]?.expires_at ?? null }
}

// Plan purchase for this row while a store owns it; data add-ons (time 0) and
// top-up stay on sale.
export function storeManagedPlan(service: any, { mode, time }: { mode: string, time: number }): StoreManagement | null {
  if (mode !== 'buyService' || time === 0) return null
  return storeManagement(service)
}

// A managedByStore refusal, recognized by its message; its detail may be
// missing. Null for other errors.
export function storeRefusal(err: any): StoreManagement | null {
  if (err?.data?.message !== MANAGED_BY_STORE) return null
  const detail = err.data.detail
  return { store: detail?.store ?? null, expiresAt: detail?.expires_at ?? null }
}

// Fill what a refusal left out from the reloaded services row.
export function withServiceRow(refusal: StoreManagement, service: any): StoreManagement {
  const row = storeManagement(service)
  return { store: refusal.store ?? row?.store ?? null, expiresAt: refusal.expiresAt ?? row?.expiresAt ?? null }
}

// Same calendar as the service end date on the dashboard: Beijing time,
// zh YYYY/MM/DD, otherwise DD/MM/YYYY. Null when there is no usable date.
export function formatStoreExpiry(expiresAt: string | null, locale: string): string | null {
  const time = expiresAt ? new Date(expiresAt) : null
  if (!time || Number.isNaN(time.getTime())) return null
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(time).map(part => [part.type, part.value]),
  )
  return locale === 'zh'
    ? `${parts.year}/${parts.month}/${parts.day}`
    : `${parts.day}/${parts.month}/${parts.year}`
}

export function storeManagedText(t: Translate, managed: StoreManagement, locale: string): string {
  const date = formatStoreExpiry(managed.expiresAt, locale)
  const form = date ? 'until' : 'plain'
  if (knownStore(managed.store)) return t(`store_managed.${managed.store}.${form}`, { date })
  if (managed.store) return t(`store_managed.other.${form}`, { store: managed.store, date })
  return t(`store_managed.unknown.${form}`, { date })
}

export function storeSubscriptionsLink(t: Translate, store: string | null): { href: string, label: string } | null {
  if (!knownStore(store)) return null
  return { href: STORES[store].subscriptionsUrl, label: t(`store_managed.${store}.manage`) }
}
